import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { errorMessage, useToast } from '@/components/Toast';
import { Badge, Button, ButtonGroup, Card, Checkbox, EmptyState, Modal, Notice, PageHeader, Select, Spinner, Textarea } from '@/components/ui';
import { Icon } from '@/components/icons';
import { formatDate } from '@/lib/format';
import { saveBlob } from '@/lib/useDownload';
import { authorityKeyOf, sourceHref } from '@/lib/passage';
import { WatchToggle } from '@/components/WatchToggle';
import { ChatWelcome } from '@/components/ChatWelcome';
import { rememberAudience, storedAudience } from '@/lib/starterPrompts';
import type { ChatAudience, ChatMessageResponse, ChatStreamStatus, Conversation } from '@/lib/types';

/** The in-flight exchange, tagged with the conversation it belongs to so a
 *  streaming answer never renders under a thread the user switched to.
 *  `user` is absent when regenerating (no new user turn is created). */
interface PendingPair {
  convId: string;
  user?: string;
  assistant: string;
  /** Server-reported stage before the first token (real progress, not a timer). */
  status?: string;
}

/** Human text for a stream `status` frame. */
function describeStatus(st: ChatStreamStatus, researchOn: boolean): string {
  if (st.stage === 'retrieving') return 'Searching the library and your documents';
  if (st.stage === 'retrieved') {
    const a = st.authorities ?? 0;
    const d = st.documents ?? 0;
    if (a === 0 && d === 0) return researchOn ? 'No authorities found — answering with caveats' : 'Drafting a response';
    const parts = [];
    if (a > 0) parts.push(`${a} authorit${a === 1 ? 'y' : 'ies'}`);
    if (d > 0) parts.push(`${d} document${d === 1 ? '' : 's'}`);
    return `Read ${parts.join(' and ')} — drafting a response`;
  }
  if (st.stage === 'generating') return 'Drafting a response';
  return 'Thinking';
}

/** Formats offered by the per-message download menu (mirrors the backend ExportFormat). */
type MessageExportFormat = 'MARKDOWN' | 'TXT' | 'PDF' | 'DOCX';

/**
 * Whether an answer reads like a standalone document (letter, contract, memo…)
 * worth offering as a file download: any Markdown heading, or substantial length.
 */
function looksLikeDocument(content: string): boolean {
  return /^#{1,3}\s/m.test(content) || content.length >= 400;
}

export function ChatPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { status, isStaff, user } = useAuth();
  const anon = status === 'anonymous';
  // The open conversation is addressable: /chat/:conversationId (with ?matter=
  // preserved). The landing page (/) stays URL-stable — its auto-selected first
  // conversation is held locally so a guest's first visit never rewrites the URL.
  const { conversationId = null } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [autoSelected, setAutoSelected] = useState<string | null>(null);
  const selected = conversationId ?? autoSelected;
  const [draft, setDraft] = useState('');
  const [providerKey, setProviderKey] = useState('');
  const [useResearch, setUseResearch] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [pending, setPending] = useState<PendingPair | null>(null);
  const [confirmDetach, setConfirmDetach] = useState<string | null>(null);
  // Mobile-only pane switch (≤900px shows one pane at a time; see index.css).
  const [showList, setShowList] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  // Synchronous re-entry guard: set at the very top of send() before any await
  // so a second Enter (or a double-click) can't spawn a duplicate conversation.
  const inFlightRef = useRef(false);
  // Whether the thread is scrolled to (near) the bottom; only then do we autoscroll.
  const pinnedRef = useRef(true);

  // Matter workspace mode (Roadmap §3.1): /?matter=<id> scopes this page to one
  // matter — conversations are listed from and created under it, and answers
  // ground in its uploaded case documents. Matters are signed-in only, so the
  // param is ignored for guests rather than failing every call.
  const [searchParams] = useSearchParams();
  const matterId = anon ? null : searchParams.get('matter');
  const urlQuery = searchParams.toString();
  /** Explicit selection (click, create) goes into the URL so it can be linked and reloaded. */
  const setSelected = useCallback((id: string | null) => {
    setAutoSelected(null);
    navigate({ pathname: id ? `/chat/${id}` : '/', search: urlQuery ? `?${urlQuery}` : '' });
  }, [navigate, urlQuery]);
  const matter = useQuery({
    queryKey: ['matter', matterId],
    queryFn: () => api.matters.get(matterId as string),
    enabled: !!matterId,
  });

  // Conversations are per-identity: keying on the user id (or 'guest') and
  // gating on a resolved auth status stops a guest's empty list from being
  // cached and then shown to the just-restored logged-in user.
  const identity = anon ? 'guest' : user?.id ?? 'guest';
  // Title search over the list (debounced); matter-scoped lists filter client-side.
  const [search, setSearch] = useState('');
  const [searchQ, setSearchQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  const conversations = useQuery({
    queryKey: matterId ? ['matter', matterId, 'conversations'] : ['chat', 'conversations', identity, searchQ],
    queryFn: () => (matterId ? api.matters.conversations(matterId, 0, 50) : api.chat.list(0, 50, searchQ || undefined)),
    enabled: status !== 'loading',
  });
  const listed = useMemo(() => {
    const items = conversations.data?.items ?? [];
    if (!matterId || !searchQ) return items;
    const needle = searchQ.toLowerCase();
    return items.filter((c) => (c.title ?? '').toLowerCase().includes(needle));
  }, [conversations.data, matterId, searchQ]);

  // The open conversation's list row (when loaded) — for the branched-from banner.
  const selectedConv = useMemo(
    () => (conversations.data?.items ?? []).find((c) => c.id === selected) ?? null,
    [conversations.data, selected],
  );

  // Who the answers are written for. Persisted per conversation (V51); a new
  // conversation is created with whatever the toggle shows, and switching it on
  // an open thread changes every later answer. Guests can use it too — plain
  // language is the non-lawyer's front door. A new conversation starts in the
  // audience this browser last chose (a per-viewer convenience; first visit asks).
  const [audience, setAudience] = useState<ChatAudience>(() => storedAudience() ?? 'PROFESSIONAL');
  const [audienceChosen, setAudienceChosen] = useState(() => storedAudience() !== null);
  useEffect(() => {
    if (selectedConv) setAudience(selectedConv.audience ?? 'PROFESSIONAL');
  }, [selectedConv]);
  const switchAudience = useMutation({
    mutationFn: ({ id, next }: { id: string; next: ChatAudience }) => api.chat.update(id, { audience: next }),
    onSuccess: () => invalidateConversations(),
    onError: (e) => toast.push(errorMessage(e), 'danger'),
  });
  function changeAudience(next: ChatAudience) {
    setAudience(next);
    rememberAudience(next);
    setAudienceChosen(true);
    if (selected) switchAudience.mutate({ id: selected, next });
  }

  // Rename / delete from the list.
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Conversation | null>(null);
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.chat.rename(id, title),
    onSuccess: () => { setRenaming(null); invalidateConversations(); },
    onError: (e) => toast.push(errorMessage(e), 'danger'),
  });
  const removeConv = useMutation({
    mutationFn: (id: string) => api.chat.remove(id),
    onSuccess: (_v, id) => {
      setConfirmDelete(null);
      invalidateConversations();
      if (selected === id) setSelected(null);
      toast.push('Conversation deleted.', 'ok');
    },
    onError: (e) => toast.push(errorMessage(e), 'danger'),
  });

  // Entering or leaving a matter workspace switches conversation universes:
  // drop the locally auto-selected conversation (an explicit /chat/:id in the
  // URL is the user's own choice and is left alone).
  useEffect(() => {
    setAutoSelected(null);
  }, [matterId]);

  const invalidateConversations = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    if (matterId) qc.invalidateQueries({ queryKey: ['matter', matterId, 'conversations'] });
  }, [qc, matterId]);
  // Engine discovery is staff-only; non-staff use the server default engine.
  const providers = useQuery({ queryKey: ['discovery', 'providers'], queryFn: api.discovery.providers, enabled: isStaff });
  const messages = useQuery({
    queryKey: ['chat', 'messages', selected],
    queryFn: () => api.chat.messages(selected as string),
    enabled: !!selected,
  });

  // Auto-select the first conversation once loaded (locally — see above).
  useEffect(() => {
    if (!selected && conversations.data && conversations.data.items.length > 0) {
      setAutoSelected(conversations.data.items[0]!.id);
    }
  }, [conversations.data, selected]);

  // Follow the newest message only when the user is already pinned to the
  // bottom — never yank a reader who has scrolled up to review earlier text.
  useEffect(() => {
    if (!pinnedRef.current) return;
    const el = threadRef.current;
    el?.scrollTo?.({ top: el.scrollHeight });
  }, [messages.data, pending, streaming]);

  function onThreadScroll() {
    const el = threadRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }

  // Cancel any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  const createConv = useMutation({
    mutationFn: (title?: string) => api.chat.create(title, matterId ?? undefined, audience),
    onSuccess: (conv) => {
      invalidateConversations();
      setSelected(conv.id);
    },
  });

  /** Send the composer draft — or, for a follow-up chip, the given question. */
  async function send(textOverride?: string) {
    if (inFlightRef.current) return;
    const text = (textOverride ?? draft).trim();
    if (!text) return;

    // Claim the in-flight slot and clear the composer synchronously, before any
    // await, so re-entry is impossible and the draft can't be double-sent.
    inFlightRef.current = true;
    setDraft('');
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let convId = selected;

    try {
      if (!convId) {
        const conv = await createConv.mutateAsync(text.slice(0, 40));
        convId = conv.id;
      }
      setPending({ convId, user: text, assistant: '' });
      await api.chat.stream(
        convId,
        {
          message: text,
          providerKey: providerKey || undefined,
          layerOverrides: useResearch ? { research_context_enhancement: true } : undefined,
        },
        {
          signal: controller.signal,
          onStatus: (st) => setPending((p) => (p ? { ...p, status: describeStatus(st, useResearch) } : p)),
          onDelta: (chunk) => setPending((p) => (p ? { ...p, assistant: p.assistant + chunk } : p)),
          onError: (msg) => { setPending(null); toast.push(msg, 'danger'); },
        },
      );
    } catch (err) {
      // A navigation/unmount abort is expected — the backend still persisted the
      // user message, so we stay quiet and let the finally refresh the thread.
      if (!(err instanceof Error && err.name === 'AbortError')) {
        toast.push(errorMessage(err), 'danger');
      }
    } finally {
      // The user message (and possibly a partial answer) is persisted regardless
      // of how the stream ended, so always refresh the thread and the list.
      if (convId) qc.invalidateQueries({ queryKey: ['chat', 'messages', convId] });
      invalidateConversations();
      setPending(null);
      setStreaming(false);
      abortRef.current = null;
      inFlightRef.current = false;
    }
  }

  /**
   * Regenerate the last answer: the backend re-runs the conversation's last
   * user message and REPLACES the superseded assistant reply on success, so the
   * old reply is hidden while its replacement streams in.
   */
  const regenerate = useCallback(async () => {
    if (inFlightRef.current || !selected) return;
    inFlightRef.current = true;
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const convId = selected;
    setPending({ convId, assistant: '' });
    try {
      await api.chat.regenerate(
        convId,
        {
          providerKey: providerKey || undefined,
          // Same grounding as send(): without this, retrying a research-grounded answer
          // silently replaces it with an ungrounded one.
          layerOverrides: useResearch ? { research_context_enhancement: true } : undefined,
        },
        {
          signal: controller.signal,
          onStatus: (st) => setPending((p) => (p ? { ...p, status: describeStatus(st, useResearch) } : p)),
          onDelta: (chunk) => setPending((p) => (p ? { ...p, assistant: p.assistant + chunk } : p)),
          onError: (msg) => { setPending(null); toast.push(msg, 'danger'); },
        },
      );
    } catch (err) {
      if (!(err instanceof Error && err.name === 'AbortError')) {
        toast.push(errorMessage(err), 'danger');
      }
    } finally {
      qc.invalidateQueries({ queryKey: ['chat', 'messages', convId] });
      invalidateConversations();
      setPending(null);
      setStreaming(false);
      abortRef.current = null;
      inFlightRef.current = false;
    }
  }, [selected, providerKey, useResearch, qc, toast, invalidateConversations]);

  const usableProviders = providers.data?.filter((p) => p.usable) ?? [];
  // Only show the streaming exchange under the conversation it belongs to.
  const showPending = !!pending && pending.convId === selected;

  // While a regeneration streams, hide the reply it is about to replace.
  const visibleMessages = useMemo(() => {
    const list = messages.data ?? [];
    if (showPending && pending!.user === undefined && list.length > 0
        && list[list.length - 1]!.role.toUpperCase() !== 'USER') {
      return list.slice(0, -1);
    }
    return list;
  }, [messages.data, showPending, pending]);

  // "Chat with a document": attach files to the current conversation.
  const attachRef = useRef<HTMLInputElement | null>(null);
  const [attaching, setAttaching] = useState(false);
  const attachedDocs = useQuery({
    queryKey: ['chat', 'docs', selected],
    queryFn: () => api.chat.attachedDocuments(selected as string),
    enabled: !!selected && !anon,
  });

  async function attach(files: File[]) {
    if (files.length === 0) return;
    setAttaching(true);
    try {
      let convId = selected;
      if (!convId) {
        const conv = await createConv.mutateAsync(undefined);
        convId = conv.id;
        setSelected(conv.id);
      }
      await api.chat.attachDocuments(convId, files);
      qc.invalidateQueries({ queryKey: ['chat', 'docs', convId] });
      toast.push(files.length > 1 ? `${files.length} documents attached.` : 'Document attached.', 'ok');
    } catch (e) {
      toast.push(errorMessage(e), 'danger');
    } finally {
      setAttaching(false);
    }
  }

  async function detach(documentId: string) {
    if (!selected) return;
    setConfirmDetach(null);
    try {
      await api.chat.detachDocument(selected, documentId);
      qc.invalidateQueries({ queryKey: ['chat', 'docs', selected] });
    } catch (e) {
      toast.push(errorMessage(e), 'danger');
    }
  }

  return (
    <>
      <PageHeader
        title={matterId ? `Chat · ${matter.data?.name ?? 'Matter'}` : 'Chat'}
        description={matterId
          ? 'Workspace chat: answers ground in this matter’s case documents, with legal authority from the research library.'
          : 'Ask legal questions and get grounded, streamed answers. Optionally pin an engine or ground the reply in the research library.'}
        actions={<Button onClick={() => { setShowList(false); createConv.mutate(undefined); }} loading={createConv.isPending}><Icon.Plus /> New chat</Button>}
      />

      {matterId && (
        <div className="row gap-2 mb-3 t-sm items-center">
          <Link to={`/matters/${matterId}`} className="inline-link">← Back to matter</Link>
          <Badge tone="accent" dot>Matter workspace</Badge>
        </div>
      )}

      {anon && (
        <div className="muted mb-3 t-sm">
          You're chatting as a guest — your conversations are saved to this browser.{' '}
          <NavLink to="/login">Log in</NavLink> or <NavLink to="/register">create an account</NavLink> to keep them across devices.
        </div>
      )}

      <div className="chat-pane-toggle" role="tablist" aria-label="Chat panes">
        <button role="tab" aria-selected={showList} className={showList ? 'active' : ''} onClick={() => setShowList(true)}>
          Conversations
        </button>
        <button role="tab" aria-selected={!showList} className={!showList ? 'active' : ''} onClick={() => setShowList(false)}>
          Chat
        </button>
      </div>

      <div className={`chat ${showList ? 'show-list' : ''}`}>
        <Card className="chat-list">
          <div className="conv-search">
            <input
              className="input"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations"
              aria-label="Search conversations"
            />
          </div>
          {conversations.isLoading ? (
            <Spinner />
          ) : listed.length > 0 ? (
            listed.map((c) => (
              <div
                key={c.id}
                className={`conv-item ${selected === c.id ? 'active' : ''}`}
                onClick={() => { if (renaming?.id !== c.id) { setSelected(c.id); setShowList(false); } }}
              >
                {renaming?.id === c.id ? (
                  <form
                    className="row gap-1"
                    onSubmit={(e) => { e.preventDefault(); if (renaming.title.trim()) rename.mutate({ id: c.id, title: renaming.title.trim() }); }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      className="input"
                      value={renaming.title}
                      onChange={(e) => setRenaming({ id: c.id, title: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                      aria-label="Conversation title"
                      maxLength={200}
                      autoFocus
                    />
                    <Button size="sm" type="submit" loading={rename.isPending} aria-label="Save title"><Icon.Check /></Button>
                    <Button size="sm" variant="ghost" type="button" onClick={() => setRenaming(null)} aria-label="Cancel rename"><Icon.Close /></Button>
                  </form>
                ) : (
                  <>
                    <div className="t truncate">{c.title || 'Untitled conversation'}</div>
                    <div className="row gap-1 items-center">
                      <span className="d grow">{formatDate(c.updatedAt)}</span>
                      <span className="conv-actions row gap-1" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="msg-action" aria-label={`Rename ${c.title || 'conversation'}`}
                                onClick={() => setRenaming({ id: c.id, title: c.title ?? '' })}><Icon.Edit /></button>
                        <button type="button" className="msg-action" aria-label={`Delete ${c.title || 'conversation'}`}
                                onClick={() => setConfirmDelete(c)}><Icon.Trash /></button>
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))
          ) : (
            <EmptyState title={searchQ ? 'No matches' : 'No conversations'} hint={searchQ ? 'Try a different search.' : 'Start a new chat to begin.'} />
          )}
        </Card>

        {confirmDelete && (
          <Modal
            title="Delete this conversation?"
            onClose={() => setConfirmDelete(null)}
            footer={
              <>
                <Button variant="secondary" onClick={() => setConfirmDelete(null)}>Keep</Button>
                <Button variant="danger" onClick={() => removeConv.mutate(confirmDelete.id)} loading={removeConv.isPending}>Delete</Button>
              </>
            }
          >
            <p className="muted t-sm">
              “{confirmDelete.title || 'Untitled conversation'}” and all of its messages will be removed. This cannot be undone.
            </p>
          </Modal>
        )}

        <Card className="chat-conv">
          {selectedConv?.branchedFromConversationId && (
            <div className="row gap-1 muted" style={{ padding: 'var(--s2) var(--s4) 0', fontSize: 'var(--fs-xs)', alignItems: 'center' }}>
              <Icon.Branch />
              <span>
                Branched from{' '}
                <Link to={`/chat/${selectedConv.branchedFromConversationId}${urlQuery ? `?${urlQuery}` : ''}`} className="inline-link">
                  the original conversation
                </Link>
              </span>
            </div>
          )}
          {/* The thread + composer always render so a new visitor can just start
              typing — send() creates the conversation automatically on first send. */}
          <div
            className="chat-thread"
            ref={threadRef}
            onScroll={onThreadScroll}
            role="log"
            aria-live="polite"
            aria-busy={streaming}
          >
            {selected && messages.isLoading ? (
              <Spinner />
            ) : (
              <>
                {visibleMessages.map((m, i) => {
                  const last = i === visibleMessages.length - 1 && m.role.toUpperCase() !== 'USER';
                  return (
                    <MessageBubble
                      key={m.id}
                      m={m}
                      convId={selected ?? pending?.convId ?? ''}
                      isLast={last}
                      busy={last ? streaming : false}
                      onRegenerate={last ? () => void regenerate() : undefined}
                      onAsk={last && !streaming ? (q) => void send(q) : undefined}
                    />
                  );
                })}
                {showPending && (
                  <>
                    {pending!.user !== undefined && <Row role="user" content={pending!.user} />}
                    <Row role="assistant" content={pending!.assistant} streaming status={pending!.status} />
                  </>
                )}
                {!showPending && (messages.data ?? []).length === 0 && (
                  <ChatWelcome
                    audience={audience}
                    chosen={audienceChosen}
                    inMatter={!!matterId}
                    onChooseAudience={changeAudience}
                    onAsk={(q) => void send(q)}
                    disabled={streaming}
                  />
                )}
              </>
            )}
          </div>

          <div className="stack gap-2" style={{ padding: '0 var(--s4)' }}>
            <div className="row gap-3 wrap" style={{ paddingTop: 'var(--s3)' }}>
              {isStaff && (
                <Select value={providerKey} onChange={(e) => setProviderKey(e.target.value)} style={{ width: 'auto', minWidth: 160 }} aria-label="Engine">
                  <option value="">Default engine</option>
                  {usableProviders.map((p) => <option key={p.key} value={p.key}>{p.displayName}</option>)}
                </Select>
              )}
              <ButtonGroup<ChatAudience>
                ariaLabel="Audience"
                value={audience}
                onChange={changeAudience}
                options={[
                  { value: 'PROFESSIONAL', label: 'For a lawyer', title: 'Precise, citation-forward answers' },
                  { value: 'PLAIN', label: 'Plain language', title: 'The same grounded answer, explained for someone without legal training' },
                ]}
              />
              <Checkbox label="Ground in research library" checked={useResearch} onChange={setUseResearch} />
              {!anon && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => attachRef.current?.click()} loading={attaching}>
                    <Icon.Doc /> Attach document
                  </Button>
                  <input ref={attachRef} type="file" multiple hidden onChange={(e) => { void attach(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
                </>
              )}
            </div>
            {!anon && (attachedDocs.data?.length ?? 0) > 0 && (
              <div className="row gap-2 wrap t-xs">
                <span className="muted">Grounded in:</span>
                {attachedDocs.data!.map((d) => (
                  <Badge key={d.id} tone="accent">
                    {d.originalFilename}
                    <button
                      onClick={() => (confirmDetach === d.id ? void detach(d.id) : setConfirmDetach(d.id))}
                      title={confirmDetach === d.id ? 'Confirm detach' : 'Detach'}
                      aria-label={confirmDetach === d.id ? `Confirm detach ${d.originalFilename}` : `Detach ${d.originalFilename}`}
                      style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit' }}
                    >{confirmDetach === d.id ? 'Confirm?' : '×'}</button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="chat-compose">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask ALFIE…  (Enter to send, Shift+Enter for a new line)"
              rows={2}
              style={{ minHeight: 44 }}
              onKeyDown={(e) => {
                // Don't send while an IME composition is in progress (e.g. Japanese/
                // Chinese input) — Enter there commits the candidate, not the message.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Button onClick={() => void send()} disabled={!draft.trim()} loading={streaming} aria-label="Send">
              <Icon.Send />
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}

/** Assistant Markdown, GFM-flavoured; links open in a new tab. */
function Markdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

/**
 * Pre-token status. Shows the stage the SERVER reported (retrieving → read N
 * authorities → drafting); only when nothing has been reported for a while does
 * it fall back to a generic reassurance — never a fake sequence of stages.
 */
function ThinkingIndicator({ status }: { status?: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 20_000);
    return () => clearTimeout(t);
  }, [status]);
  const text = status ?? (slow ? 'Still working — long answers take a moment' : 'Thinking');
  return (
    <span className="thinking" role="status" aria-label="Assistant status">
      {text}
      <span className="dots" aria-hidden><i /><i /><i /></span>
    </span>
  );
}

/** A transient (streaming) exchange row; persisted rows use MessageBubble. */
function Row({ role, content, streaming, status }: { role: 'user' | 'assistant'; content: string; streaming?: boolean; status?: string }) {
  const thinking = role === 'assistant' && !!streaming && !content;
  return (
    <div className={`msg ${role}`}>
      <span className="who">{role === 'user' ? 'You' : 'AI'}</span>
      <div className={`bubble ${role === 'assistant' && !thinking ? 'md' : ''}`}>
        {thinking ? (
          <ThinkingIndicator status={status} />
        ) : role === 'assistant' ? (
          <>
            <Markdown content={content} />
            {streaming && <span className="spinner" style={{ width: 12, height: 12 }} aria-hidden />}
          </>
        ) : (
          content
        )}
      </div>
    </div>
  );
}

/** Copy / download / save / branch / regenerate controls under an assistant answer. */
function MessageActions({ m, convId, isLast, busy, onRegenerate }: {
  m: ChatMessageResponse;
  convId: string;
  isLast?: boolean;
  busy?: boolean;
  onRegenerate?: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useAuth();
  const signedIn = status === 'authenticated';
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branching, setBranching] = useState(false);
  const [saving, setSaving] = useState(false);

  /** File the answer as an editable, versioned document and open it. */
  async function saveAsDocument() {
    setSaving(true);
    try {
      const doc = await api.chat.saveAsDocument(convId, m.id);
      qc.invalidateQueries({ queryKey: ['docgen'] });
      toast.push('Saved to your documents — editable, versioned, exportable.', 'ok');
      navigate(`/documents/${doc.id}`);
    } catch (e) {
      toast.push(errorMessage(e), 'danger');
    } finally {
      setSaving(false);
    }
  }
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(m.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.push('Could not copy to the clipboard.', 'danger');
    }
  }

  /** Fork the conversation at this answer and jump to the new thread. */
  async function branch() {
    setBranching(true);
    try {
      const conv = await api.chat.branch(convId, m.id);
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['matter'] });
      toast.push('Branched — you are now on the new thread.', 'ok');
      navigate({ pathname: `/chat/${conv.id}`, search: location.search });
    } catch (e) {
      toast.push(errorMessage(e), 'danger');
    } finally {
      setBranching(false);
    }
  }

  async function download(format: MessageExportFormat) {
    setMenuOpen(false);
    setExporting(true);
    try {
      const { blob, filename } = await api.chat.exportMessage(convId, m.id, format);
      saveBlob(blob, filename);
    } catch (e) {
      toast.push(errorMessage(e), 'danger');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="msg-actions">
      <button className="msg-action" onClick={() => void copy()} aria-label="Copy message">
        {copied ? <Icon.Check /> : <Icon.Copy />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      {looksLikeDocument(m.content) && (
        <div className="msg-menu" ref={menuRef}>
          <button
            className="msg-action"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Download message"
          >
            {exporting ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Icon.Download />}
            Download
          </button>
          {menuOpen && (
            <div className="msg-menu-list" role="menu">
              <button className="msg-menu-item" role="menuitem" onClick={() => void download('PDF')}><Icon.Doc /> PDF</button>
              <button className="msg-menu-item" role="menuitem" onClick={() => void download('DOCX')}><Icon.Doc /> Word (.docx)</button>
              <button className="msg-menu-item" role="menuitem" onClick={() => void download('MARKDOWN')}><Icon.Doc /> Markdown (.md)</button>
              <button className="msg-menu-item" role="menuitem" onClick={() => void download('TXT')}><Icon.Doc /> Plain text (.txt)</button>
            </div>
          )}
        </div>
      )}
      {signedIn && (
        <button className="msg-action" onClick={() => void saveAsDocument()} disabled={saving || busy}
                aria-label="Save answer as a document" title="File this answer in your documents: editable, versioned, exportable">
          {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Icon.Save />}
          Save
        </button>
      )}
      <button className="msg-action" onClick={() => void branch()} disabled={branching || busy}
              aria-label="Branch the conversation from this answer" title="Start a new thread that keeps everything up to here">
        <Icon.Branch /> Branch
      </button>
      {isLast && onRegenerate && (
        <button className="msg-action" onClick={onRegenerate} disabled={busy} aria-label="Regenerate response">
          <Icon.Refresh /> Retry
        </button>
      )}
    </div>
  );
}

// Memoized so a per-token `setPending` re-render of the page doesn't re-render
// every persisted bubble in the (potentially long) thread.
const MessageBubble = memo(function MessageBubble({ m, convId, isLast, busy, onRegenerate, onAsk }: {
  m: ChatMessageResponse;
  convId: string;
  isLast?: boolean;
  busy?: boolean;
  onRegenerate?: () => void;
  /** Present on the last answer only: asking a suggested follow-up sends it. */
  onAsk?: (question: string) => void;
}) {
  const role = m.role.toUpperCase() === 'USER' ? 'user' : 'assistant';
  return (
    <div className={`msg ${role}`}>
      <span className="who">{role === 'user' ? 'You' : 'AI'}</span>
      <div className="stack gap-1" style={{ maxWidth: '100%' }}>
        <div className={`bubble ${role === 'assistant' ? 'md' : ''}`}>
          {role === 'assistant' ? <Markdown content={m.content} /> : m.content}
        </div>
        {role === 'assistant' && (
          <MessageActions m={m} convId={convId} isLast={isLast} busy={busy} onRegenerate={onRegenerate} />
        )}
        {role === 'assistant' && <Grounding json={m.groundingJson} />}
        {role === 'assistant' && <Citations json={m.citationsJson} />}
        {role === 'assistant' && onAsk && <FollowUps m={m} convId={convId} onAsk={onAsk} />}
        {role === 'assistant' && (m.providerUsed || m.modelUsed) && (
          <div className="row gap-2 t-xs">
            {m.providerUsed && <Badge tone="neutral">{m.providerUsed}{m.modelUsed ? ` · ${m.modelUsed}` : ''}</Badge>}
            {m.latencyMs != null && <span className="muted">{m.latencyMs} ms</span>}
            {m.tokensOut != null && <span className="muted">· {m.tokensOut} tok</span>}
          </div>
        )}
      </div>
    </div>
  );
});

interface Citation {
  title?: string;
  label?: string;
  citation?: string;
  source?: string;
  url?: string;
  sourceId?: string;
  /** The authority wherever it lives (`uplaw:<did>` in the law library, or a local id) — opens the reader. */
  authorityKey?: string;
  /** The passage the model was shown (trimmed) — the source card's body. */
  excerpt?: string;
  negativeTreatment?: boolean;
  treatmentSummary?: string;
}

interface GroundingFlag {
  citation: string;
  issue: string;
  detail?: string;
}
interface GroundingReport {
  grounded: boolean;
  citationsChecked: number;
  ungrounded: number;
  unsupported: number;
  flags: GroundingFlag[];
}

/**
 * A caution shown when an answer's citations aren't fully grounded in the retrieved sources:
 * UNGROUNDED = the answer cited authority that wasn't retrieved (possibly fabricated); UNSUPPORTED
 * = the cited source may not support the statement. Silent when the answer is fully grounded.
 */
/** Parse the cached follow-up list off a message; null when never requested. */
function parseFollowups(json: string | null | undefined): string[] | null {
  if (json == null) return null;
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === 'string') : null;
  } catch {
    return null;
  }
}

/**
 * Suggested next questions under the LAST answer. Served from the message when
 * already cached; otherwise one request (the server makes a single small model
 * call and caches it on the row). Fail-soft: no chips on error, no noise.
 */
function FollowUps({ m, convId, onAsk }: { m: ChatMessageResponse; convId: string; onAsk: (q: string) => void }) {
  const cached = parseFollowups(m.followupsJson);
  const fetched = useQuery({
    queryKey: ['chat', 'followups', convId, m.id],
    queryFn: () => api.chat.followups(convId, m.id),
    enabled: cached === null && !!convId,
    staleTime: Infinity,
    retry: false,
  });
  const questions = cached ?? fetched.data?.questions ?? [];
  if (questions.length === 0) return null;
  return (
    <div className="followups" role="group" aria-label="Suggested follow-up questions">
      <span className="muted t-xs">Ask next:</span>
      {questions.map((q) => (
        <button key={q} type="button" className="followup" onClick={() => onAsk(q)}>{q}</button>
      ))}
    </div>
  );
}

function Grounding({ json }: { json: string | null }) {
  if (!json) return null;
  let report: GroundingReport;
  try {
    report = JSON.parse(json) as GroundingReport;
  } catch {
    return null;
  }
  if (!report.flags || report.flags.length === 0) return null;

  const ungrounded = report.flags.filter((f) => f.issue === 'UNGROUNDED');
  const unsupported = report.flags.filter((f) => f.issue === 'UNSUPPORTED');
  // Ungrounded citations are the severe case (authority that was never
  // retrieved); an unsupported quote is a warning.
  const severe = ungrounded.length > 0;

  return (
    <Notice tone={severe ? 'danger' : 'warn'} title="This answer may not be fully grounded">
      {ungrounded.length > 0 && (
        <span>
          Cites authority that wasn't in the retrieved sources:{' '}
          {ungrounded.map((f, i) => <span key={i}>{i > 0 ? ', ' : ''}<strong>{f.citation}</strong></span>)}. It
          may be inaccurate — verify independently.
        </span>
      )}
      {unsupported.length > 0 && (
        <span>
          The cited source may not support the statement for:{' '}
          {unsupported.map((f, i) => <span key={i}>{i > 0 ? ', ' : ''}<strong>{f.citation}</strong></span>)}.
        </span>
      )}
    </Notice>
  );
}

/** Parse the persisted `citationsJson` blob into a list, tolerating null/invalid JSON. */
function parseCitations(json: string | null): (string | Citation)[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as (string | Citation)[]) : [];
  } catch {
    return [];
  }
}

function citationLabel(c: string | Citation): string {
  if (typeof c === 'string') return c;
  return c.title || c.label || c.citation || c.source || c.url || 'Source';
}

function isNegative(c: string | Citation): boolean {
  return typeof c === 'object' && c.negativeTreatment === true;
}

/**
 * Sources under an assistant answer: one card per retrieved authority with the
 * passage the model was actually shown (expand to read it), the good-law
 * caution where one applies, and a watch toggle (signed-in) so a later change
 * to that authority raises an alert.
 */
function Citations({ json }: { json: string | null }) {
  const cites = parseCitations(json);
  if (cites.length === 0) return null;
  const flagged = cites.filter(isNegative);
  return (
    <div className="stack gap-1 t-xs">
      {flagged.length > 0 && <TreatmentCaution flagged={flagged} />}
      <span className="eyebrow">Sources</span>
      <ol className="stack gap-1">
        {cites.map((c, i) => <SourceCard key={i} n={i + 1} c={c} />)}
      </ol>
    </div>
  );
}

function SourceCard({ n, c }: { n: number; c: string | Citation }) {
  const [open, setOpen] = useState(false);
  const { status } = useAuth();
  const label = citationLabel(c);
  const obj = typeof c === 'object' ? c : undefined;
  const citation = obj?.citation && obj.citation !== label ? obj.citation : undefined;
  const negative = isNegative(c);
  return (
    <li className={`source-card ${negative ? 'negative' : ''}`}>
      <div className="row gap-2 items-baseline wrap">
        <span className="muted mono">[{n}]</span>
        {obj?.url
          ? <a href={obj.url} className="inline-link" target="_blank" rel="noreferrer">{label}</a>
          : obj && authorityKeyOf(obj) && status === 'authenticated'
            // An authority opens in the case reader, at the passage the answer was grounded in.
            ? <Link to={sourceHref(authorityKeyOf(obj) as string, obj.excerpt)} className="inline-link fw-semibold">{label}</Link>
            : <span className="fw-semibold">{label}</span>}
        {citation && <span className="muted">{citation}</span>}
        <span className="grow" />
        {obj?.excerpt && (
          <button type="button" className="inline-link"
                  onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? 'Hide passage' : 'Show passage'}
          </button>
        )}
        {obj?.sourceId && <WatchToggle sourceId={obj.sourceId} />}
      </div>
      {negative && (
        <div className="text-danger t-xs fw-medium">
          Negative treatment{obj?.treatmentSummary ? ` — ${obj.treatmentSummary}` : ''}
        </div>
      )}
      {open && obj?.excerpt && (
        <blockquote className="source-excerpt">{obj.excerpt}</blockquote>
      )}
    </li>
  );
}

/**
 * A caution banner shown when an answer relies on authority that later opinions have
 * overruled or criticized — the same signal the model was given, surfaced to the reader.
 */
function TreatmentCaution({ flagged }: { flagged: (string | Citation)[] }) {
  return (
    <Notice tone="danger" title="This answer cites authority with negative treatment">
      <span>
        {flagged.length === 1 ? 'One cited authority has' : `${flagged.length} cited authorities have`}{' '}
        been overruled or criticized by later opinions and may no longer be good law. Verify current
        status before relying on{' '}
        {flagged.map((c, i) => (
          <span key={i}>
            {i > 0 ? ', ' : ''}
            <strong>{citationLabel(c)}</strong>
          </span>
        ))}
        .
      </span>
    </Notice>
  );
}
